import { useState } from "react"
import { MenuItem } from "@mui/material"
import { useFormikContext } from "formik"
import { fetchSalesAccounts, SalesAccountsQuery, SalesAccountsResponse } from "../../salesAccounts/salesAccountsApi"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"
import { fetchShopsList, ShopsListResponse, ShopsQuery } from "../../shops/shopsApi"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { CreateUpdatePaymentLinkFormValues } from "../paymentLinksTypes"
import { AvailabilityDetailsField } from "./AvailabilityDetailsField"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import {
    FormikDropdown,
    FormikPasswordField,
    FormikSwitch,
    FormikTextField,
} from "../../../../../shared/components/forms/Formik"

const selectSalesAccounts = (data: SalesAccountsResponse) => data?.salesAccounts || []
const selectShops = (data: ShopsListResponse) => data?.shops || []

export function CreateUpdatePaymentLinkFormContent() {
    const { merchantId } = useMerchant()

    const { values, setFieldValue } = useFormikContext<CreateUpdatePaymentLinkFormValues>()
    const [checkedAvailability, setCheckedAvailability] = useState<boolean>(!!values?.availabilityDetails.dateInterval)

    const { data: shopsList = [] } = useApiQuery(fetchShopsList, {
        queryName: ShopsQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectShops,
    })

    const { data: salesAccountsList = [] } = useApiQuery(fetchSalesAccounts, {
        queryName: SalesAccountsQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectSalesAccounts,
    })

    const handleChangeAvailability = (checked: boolean) => {
        setCheckedAvailability(checked)
        if (!checked) setFieldValue("availabilityDetails.dateInterval", undefined)
    }

    return (
        <>
            <StandardForm>
                <StandardMainPanel>
                    <LocalizedStrict id="payment-links-form-public-title-label" attrs={{ label: true }}>
                        <FormikTextField<CreateUpdatePaymentLinkFormValues> name="title" label="Title" fullWidth />
                    </LocalizedStrict>
                    <LocalizedStrict id="payment-links-form-description-label" attrs={{ label: true }}>
                        <FormikTextField<CreateUpdatePaymentLinkFormValues>
                            name="description"
                            label="Description"
                            fullWidth
                        />
                    </LocalizedStrict>
                    <LocalizedStrict id="payment-links-form-public-shop-field-label" attrs={{ label: true }}>
                        <FormikDropdown name="shop.id" label="Public shop" fullWidth>
                            {shopsList.map((shop) => (
                                <MenuItem key={shop.id} value={shop.id}>
                                    {shop.name}
                                </MenuItem>
                            ))}
                        </FormikDropdown>
                    </LocalizedStrict>
                    <LocalizedStrict id="payment-links-form-sales-account-field-label" attrs={{ label: true }}>
                        <FormikDropdown name="salesAccount.id" label="Sales account" fullWidth>
                            {salesAccountsList.map((salesAccount) => (
                                <MenuItem key={salesAccount.id} value={salesAccount.id}>
                                    {salesAccount.number} - {salesAccount.name}
                                </MenuItem>
                            ))}
                        </FormikDropdown>
                    </LocalizedStrict>
                </StandardMainPanel>
                <StandardMainPanel>
                    <LocalizedStrict id="payment-links-form-hasPasswordProtection-switch">
                        <FormikSwitch name="security.hasPasswordProtection">Password protected</FormikSwitch>
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="payment-links-form-password-field-label"
                        attrs={{ label: true, helperText: true }}
                    >
                        <FormikPasswordField name="security.password" label="Password" fullWidth />
                    </LocalizedStrict>
                </StandardMainPanel>
                <LocalizedStrict id="payment-links-form-general-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="General" variant="collapsible">
                        <LocalizedStrict id="payment-links-form-active-switch">
                            <FormikSwitch<CreateUpdatePaymentLinkFormValues> name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>

                <LocalizedStrict id="payment-links-form-availability-panel" attrs={{ title: true, offContent: true }}>
                    <StandardSidePanel
                        title="Availability"
                        variant="switchable"
                        checked={checkedAvailability}
                        onCheckChange={handleChangeAvailability}
                        offContent="Valid indefinitely"
                    >
                        <AvailabilityDetailsField />
                    </StandardSidePanel>
                </LocalizedStrict>
            </StandardForm>
        </>
    )
}
